import { useEffect, useMemo, useRef, useState } from 'react';
import {
  MiniMap,
  NodeResizer,
  ReactFlow,
  useEdgesState,
  useNodesState,
  useReactFlow,
  useUpdateNodeInternals,
  type Edge,
  type EdgeTypes,
  type NodeProps,
  type NodeTypes,
  type Viewport,
} from '@xyflow/react';
import { Locate, Minus, Plus } from 'lucide-react';
import { ModuloLicitacao, type ModuloLayoutDto } from '@nexital/shared';
import { layoutDosModulos } from '../../domain/licitacao';
import { ArestaSeta } from '../notas/ArestaSeta';
import { AvisoNotas } from '../notas/AvisoNotas';
import { manterSelecao, type NoNotaTipo } from '../notas/montar-nos-notas';
import { useFocoNaNota } from '../notas/use-foco-nota';
import { NoNota } from '../notas/NoNota';
import { BarraFerramentas, type Ferramenta } from '../quadro/BarraFerramentas';
import { useDependencias } from '../../application/dependencias';
import { useLicitacao } from './contexto';
import { ModuloCronograma } from './ModuloCronograma';
import { ModuloDocumentos } from './ModuloDocumentos';
import { ModuloHistorico } from './ModuloHistorico';
import { ModuloNotas } from './ModuloNotas';
import { ModuloPassos } from './ModuloPassos';
import { ModuloPessoas } from './ModuloPessoas';
import { ModuloSobre } from './ModuloSobre';
import { useInteracaoLicitacao, useNotasLicitacao, type NoLicitacao, type NoModulo } from './use-notas-licitacao';
import { usePanComCtrl, useTeclasDoQuadro } from '../notas/use-teclas-quadro';

const COMPONENTE: Record<ModuloLicitacao, () => React.JSX.Element> = {
  [ModuloLicitacao.Sobre]: ModuloSobre,
  [ModuloLicitacao.Pessoas]: ModuloPessoas,
  [ModuloLicitacao.Documentos]: ModuloDocumentos,
  [ModuloLicitacao.Notas]: ModuloNotas,
  [ModuloLicitacao.ProximosPassos]: ModuloPassos,
  [ModuloLicitacao.Cronograma]: ModuloCronograma,
  [ModuloLicitacao.Historico]: ModuloHistorico,
};

/** Módulo no quadro da licitação, redimensionável pelas bordas quando selecionado (tamanho no layout da pessoa). */
function NoDoModulo({ data, selected }: NodeProps<NoModulo>) {
  const Componente = COMPONENTE[data.modulo];
  const mover = useLicitacao((s) => s.moverModulos);
  return (
    <>
      <NodeResizer
        isVisible={selected}
        minWidth={280}
        minHeight={180}
        lineClassName="modulo__redimensionar"
        handleClassName="modulo__alca"
        onResizeEnd={(_, p) => void mover([{ modulo: data.modulo, x: p.x, y: p.y, largura: Math.round(p.width), altura: Math.round(p.height) }])}
      />
      <Componente />
    </>
  );
}

const TIPOS: NodeTypes = { modulo: NoDoModulo, nota: NoNota };
const TIPOS_ARESTA: EdgeTypes = { seta: ArestaSeta };
const SEM_ATIVIDADES: never[] = [];

/** Nós arrastados ficam como estão até soltar e a seleção sobrevive à releitura (as alças não somem ao gravar). */
const preservarArrastados = (novos: NoLicitacao[]) => (atuais: NoLicitacao[]) => {
  const porId = new Map(atuais.map((n) => [n.id, n]));
  return novos.map((n) => {
    const atual = porId.get(n.id);
    if (atual?.dragging) return atual;
    return atual?.selected ? { ...n, selected: true } : n;
  });
};

function Controles() {
  const { zoomIn, zoomOut, fitView } = useReactFlow();
  return (
    <div className="controles controles--licitacao">
      <MiniMap className="controles__minimapa" nodeBorderRadius={14} maskColor="rgba(247, 246, 242, 0.55)" pannable zoomable ariaLabel="Minimapa da licitação" />
      <div className="controles__barra">
        <div className="controles__zoom">
          <button type="button" onClick={() => zoomOut({ duration: 200 })} aria-label="Afastar"><Minus size={18} /></button>
          <button type="button" onClick={() => zoomIn({ duration: 200 })} aria-label="Aproximar"><Plus size={18} /></button>
        </div>
        <button type="button" className="controles__ajustar" onClick={() => void fitView({ duration: 300, padding: 0.04 })}>
          <Locate size={18} aria-hidden /> Ajustar
        </button>
      </div>
    </div>
  );
}

/** Módulos (com a posição e o tamanho do layout da pessoa) e post-its viram nós; o que está na mão fica como está. */
function useNosDosModulos(layout: ModuloLayoutDto[], deNotas: NoNotaTipo[]) {
  const [nos, definirNos, aoMudar] = useNodesState<NoLicitacao>([]);
  useEffect(() => {
    const modulos: NoModulo[] = layout.map((m) => ({ id: String(m.modulo), type: 'modulo', position: { x: m.x, y: m.y }, data: { modulo: m.modulo }, width: m.largura, height: m.altura }));
    definirNos(preservarArrastados([...modulos, ...deNotas]));
  }, [layout, deNotas, definirNos]);
  return [nos, aoMudar] as const;
}

/** Setas da licitação com seleção própria (o Delete apaga a selecionada), mantida quando as setas são relidas. */
function useArestas(deSetas: Edge[]) {
  const [arestas, definir, aoMudar] = useEdgesState<Edge>([]);
  useEffect(() => definir(manterSelecao(deSetas)), [deSetas, definir]);
  return [arestas, aoMudar] as const;
}

/** Zoom e posição da tela de cada pessoa em cada licitação, neste navegador (ADR-0023). */
function useCameraDaLicitacao() {
  const { armazenamento, sessao } = useDependencias();
  const processoUuid = useLicitacao((s) => s.dados?.processo.uuid ?? '');
  const chave = `${sessao.usuarioUuid}:l:${processoUuid}`;
  const salva = useMemo(() => armazenamento.lerCamera(chave), [armazenamento, chave]);
  return { salva, salvar: (v: Viewport) => armazenamento.salvarCamera(chave, v) };
}

/** Itens novos (documento, passo, marco, pessoa) trazem pontas novas: o React Flow precisa medi-las de novo. */
function useMedirPontas(layout: { modulo: ModuloLicitacao }[], arestas: unknown) {
  const dados = useLicitacao((s) => s.dados);
  const atualizarPontas = useUpdateNodeInternals();
  useEffect(() => {
    const ids = layout.map((m) => String(m.modulo));
    const quadro = requestAnimationFrame(() => atualizarPontas(ids));
    return () => cancelAnimationFrame(quadro);
  }, [dados, arestas, layout, atualizarPontas]);
}

/**
 * Quadro próprio da licitação (RQ-LIC-001): módulos móveis com arranjo compartilhado e, por cima, post-its e
 * setas presos a módulos e a itens (documento, passo, marco, pessoa) como lembretes.
 */
export function QuadroDaLicitacao() {
  const salvo = useLicitacao((s) => s.dados?.layout);
  const atividades = useLicitacao((s) => s.dados?.atividades ?? SEM_ATIVIDADES);
  const mover = useLicitacao((s) => s.moverModulos);
  const [ferramenta, definirFerramenta] = useState<Ferramenta>('selecao');
  const layout = useMemo(() => layoutDosModulos(salvo ?? []), [salvo]);
  const notas = useNotasLicitacao(layout, atividades);
  const interacao = useInteracaoLicitacao({ ferramenta, definirFerramenta, layout, atividades });
  const [nos, aoMudar] = useNosDosModulos(layout, notas.nos);
  useFocoNaNota(nos);
  const [arestas, aoMudarArestas] = useArestas(notas.arestas);
  useMedirPontas(layout, notas.arestas);

  const aoSoltar = (evento: MouseEvent | TouchEvent, _: unknown, soltos: NoLicitacao[]) => {
    const modulos = soltos.filter((n): n is NoModulo => n.type === 'modulo');
    const tamanhos = new Map(layout.map((m) => [m.modulo, m]));
    if (modulos.length) void mover(modulos.map((n) => ({ ...tamanhos.get(n.data.modulo)!, modulo: n.data.modulo, x: n.position.x, y: n.position.y })));
    for (const nota of soltos.filter((n): n is NoNotaTipo => n.type === 'nota')) interacao.soltarNota(evento, nota);
  };

  const criando = ferramenta === 'nota' || ferramenta === 'conector';
  const { mao, apagarSetas } = useTeclasDoQuadro<NoLicitacao, Edge>(ferramenta);
  const tela = useRef<HTMLDivElement>(null);
  const ctrl = usePanComCtrl(tela);
  const camera = useCameraDaLicitacao();
  return (
    <div ref={tela} className={`licitacao__tela${criando ? ' licitacao__quadro--criando' : ''}${mao || ctrl ? ' quadro--mao' : ''}`}>
      <ReactFlow<NoLicitacao>
        nodes={nos}
        edges={arestas}
        onNodesChange={aoMudar}
        onEdgesChange={aoMudarArestas}
        nodeTypes={TIPOS}
        edgeTypes={TIPOS_ARESTA}
        onNodeDragStop={aoSoltar}
        onNodeClick={interacao.aoClicarNo}
        onPaneClick={interacao.aoClicarNoVazio}
        panOnDrag={mao ? true : [0, 1]}
        nodesDraggable={!criando && !mao}
        multiSelectionKeyCode="Shift"
        {...apagarSetas}
        nodesConnectable={false}
        defaultViewport={camera.salva ?? undefined}
        fitView={!camera.salva}
        fitViewOptions={{ padding: 0.04 }}
        onMoveEnd={(_, v: Viewport) => camera.salvar(v)}
        minZoom={0.2}
        maxZoom={2}
        zoomOnDoubleClick={false}
        nodeDragThreshold={4}
        proOptions={{ hideAttribution: true }}
        aria-label="Quadro da licitação"
      >
        <Controles />
      </ReactFlow>
      <BarraFerramentas ativa={ferramenta} definir={definirFerramenta} />
      <AvisoNotas />
    </div>
  );
}
